<template>
    <div>
        <RedesignDataTableComponent
            v-if="dialers?.length"
            :columns="availableColumns"
            :columns-config="currentColumnsConfig"
            :with-columns-manage="!displayTableAsRow"
            :table-data="tableData"
            :widget-data="data"
            stripe
            border
            @update-widget="updateWidget"
            @update-columns-config="updateColumnsConfig"
            @update-headers-and-columns-config="updateColumnsAndHeadersConfig"
        >
            <template
                #cell="{ row, column }"
            >
                <div>
                    <div v-if="isCallsColum(row, column)">
                        <RedesignTooltip
                            :disabled="!row[column.prop].length"
                        >
                            <template #tooltip-content>
                                <div class="grid grid-cols-2 gap-4">
                                    <div
                                        v-for="call in row[column.prop]"
                                        :key="call.CallID"
                                        class="tooltip-call-info"
                                    >
                                        <div
                                            v-for="(value, key) in call"
                                            :key="key"
                                        >
                                            <span><strong>{{ key }}: </strong>  {{ value }}</span>
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <div class="cursor-pointer">
                                <div>{{ row[column.prop].length }}</div>
                            </div>
                        </RedesignTooltip>
                    </div>
                    <div v-else-if="isRelatedStatisticColumn(row, column)">
                        <RedesignTooltip
                            :disabled="!row[column.prop]?.length"
                        >
                            <template #tooltip-content>
                                <div
                                    v-for="stat in row[column.prop]"
                                    :key="stat.CallID"
                                >
                                    <div>{{ `${stat.billing_cdr_queue_type_name}:  ${stat.TotalCalls}` }}: </div>
                                </div>
                            </template>
                            <div class="cursor-pointer">
                                <div>{{ row[column.prop].length }}</div>
                            </div>
                        </RedesignTooltip>
                    </div>
                    <span
                        v-else
                        v-html="row[column.prop]"
                    />
                </div>
            </template>
        </RedesignDataTableComponent>
    </div>
</template>

<script setup lang="ts">
import { computed, defineEmits, toRef } from 'vue'
import get from 'lodash/get'
import i18n from '@/i18n'

import RedesignDataTableComponent from '@/components/RedesignBaseComponents/RedesignDataTableComponent.vue'
import RedesignTooltip from '@/components/RedesignBaseComponents/RedesignTooltip.vue'

import { ColumnsObjectConfig, ColumnLabel, WidgetExtended, TableHeadersConfig } from '@/types/dashboard'
import { hasColumnSpecificAlignment } from '@/helpers/alignmentUtils'
import { Dialer } from '@/types/dialers'
import useTableHeaderTranslations from '@/composables/useTableHeaderTranslations'
import { getTranslationOrUndefined } from '@/helpers/getTranslation'
import startCase from 'lodash/startCase'

/* Types */
type TableData = Record<string, string | number>
interface Column {
    label: ColumnLabel,
    prop: string,
    tooltipLabel: string
}

const relatedQueStatisticKeyName = 'RelatedQueueStatistics'
const campaignIDKeyName = 'CampaignID'
const campaignNameKey = 'CampaignName'
const callsKey = 'calls'

const {
    makeTableHeaderTooltipTranslationsByProp,
    makeTableHeaderTranslationsByProp,
    makeTableHeaderTooltipTranslations,
    makeTableHeaderTranslations
} = useTableHeaderTranslations()

/* Props */
const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
        dialers: Array<Dialer>
        tableDataLoaded: TableData[]
    }>(),
    {
        editable: true
    }
)

const emit = defineEmits<{
    (event: 'update-widget', payload: WidgetExtended): void
}>()

/* Data */
const tableStatusesKeys = [ 'legOneRinging', 'legTwoRinging', 'talking' ] as const
const tableKeys = [ '#', 'campaignName', ...tableStatusesKeys ] as const
const tableKeysForGettingData = {
    '#': 'campaignID',
    campaignName: 'name',
    legOneRinging: 'data.CallStatistics.TotalLeg1RingingCalls',
    legTwoRinging: 'data.CallStatistics.TotalLeg2RingingCalls',
    talking: 'data.CallStatistics.TotalLeg2AnswerCalls'
} as const
const localWidgetData = toRef(props, 'data')
// const tableDataRef = ref(cloneDeep(props.tableDataLoaded))

/* Computed */
const getAllDialersToDisplayByCampaigns = computed(() => {
    return [ ...props.dialers ]
        .sort((a, b) => a.name !== b.name ? a.name < b.name ? -1 : 1 : 0)
})
const displayTableAsRow = computed(() => {
    return props.data.WidgetLayout?.displayTableAsRow ?? false
})
const availableColumns = computed(() => {
    if (displayTableAsRow.value) {
        return [
            {
                prop: 'additionCol',
                fixed: false,
                align: 'center'
            },
            ...getAllDialersToDisplayByCampaigns.value.map(el => {
                return {
                    prop: el.name,
                    fixed: false,
                    align: 'center',
                    tooltipLabel: makeTableHeaderTooltipTranslationsByProp(el.name),
                    label: makeTableHeaderTranslationsByProp(el.name),
                }
            })
        ]
    } else {
        const columns = tableKeys
            .map(el => {
                return {
                    prop: el,
                    fixed: false,
                    align: 'center',
                    tooltipLabel: makeTableHeaderTooltipTranslationsByProp(el),
                    label: makeTableHeaderTranslationsByProp(el),
                    sortable: true,
                }
            })
        if(loadedDataColumns.value) {
            return [ ...columns, ...loadedDataColumns.value ]
        }
        return [ ...columns ]
    }
})

const loadedDataColumns = computed(() => {
    if(props.tableDataLoaded && Object.values(props.tableDataLoaded).length) {
        const firstRecord = props.tableDataLoaded[0]
        const columns = []
        if (firstRecord) {
            for (const column in firstRecord) {
                if(column !== campaignIDKeyName && column !== campaignNameKey ) {
                    const baseLabel = getTranslationOrUndefined(`WidgetTemplateColumnName.${column}`)
                        || getTranslationOrUndefined(column)
                        || startCase(column)

                    const columnData: Column = {
                        prop: column,
                        label: {} as ColumnLabel,
                        tooltipLabel: ''
                    }

                    columnData.label = makeTableHeaderTranslations(columnData, baseLabel)
                    columnData.tooltipLabel = makeTableHeaderTooltipTranslations(columnData, baseLabel)

                    columns.push(columnData)
                }
            }
        }
        return columns
    }
    return null
})

const columnsConfig = computed(() => {
    const columnsConfig: ColumnsObjectConfig = {}
    availableColumns.value.map((column, index) => {
        columnsConfig[column.prop] = {
            hidden: false,
            showFullTextLabel: true,
            showIcon: false,
            showShortTextLabel: false,
            width: 'auto',
            order: index,
        }
    })
    return columnsConfig
})

const currentColumnsConfig = computed(() => {
    if (displayTableAsRow.value) {
        return columnsConfig.value
    }
    return tableAsNotRowColumnsConfig.value
})

const tableAsNotRowColumnsConfig = computed(() => {
    if (!props.tableDataLoaded || !props.tableDataLoaded?.length || !Object.values(loadedColumnsConfigLoaded.value)?.length) {
        return columnsConfig.value
    }
    return loadedColumnsConfigLoaded.value
})

const loadedColumnsConfigLoaded = computed(() => {
    return localWidgetData.value.WidgetLayout?.columnsObjectConfig || {}
})

const tableAsRowData = computed<Array<TableData>>(() => {
    const baseData =  tableStatusesKeys.map(key => {
        const firstColWithTranslation = i18n.t(`widget.table.header.title.full.${key}`)

        return {
            additionCol: firstColWithTranslation,
            ...getAllDialersToDisplayByCampaigns.value.reduce<TableData>(
                (acc, el) => {
                    return {
                        ...acc,
                        [el.name]: get(el, tableKeysForGettingData[key], '')
                    }
                },
                {}
            )
        } as TableData
    })
    if (loadedDataColumns.value && loadedDataColumns.value?.length) {
        const extraLoadedData = loadedDataColumns.value?.map(item => {
            const firstColumn = item.label.fullTextLabel

            return {
                additionCol: firstColumn,
                ...props.tableDataLoaded.reduce<TableData>(
                    (acc, el) => {
                        return {
                            ...acc,
                            type: item.prop,
                            [el.CampaignName]: get(el, item.prop, '')
                        }
                    },
                    {}
                )
            }
        })
        return [ ...baseData, ...extraLoadedData ]
    }
    return baseData
})
const tableAsNotRowData = computed<Array<TableData>>(() => {
    return getAllDialersToDisplayByCampaigns.value.map(dialer => {
        return tableKeys.reduce<TableData>(
            (acc, el) => {
                return {
                    ...acc,
                    [el]: get(dialer, tableKeysForGettingData[el], '')
                }
            },
            {}
        )
    })
})

const tableData = computed(() => {
    if (displayTableAsRow.value) {
        return tableAsRowData.value
    } else {
        return tableAsNotRowDataMerged.value
    }
})

const tableAsNotRowDataMerged = computed(() => {
    return tableAsNotRowData.value.map(item => {
        const dataFromRequest = props.tableDataLoaded.find(itemFromRequest => item['#'] === itemFromRequest.CampaignID)
        return {
            ...item,
            ...dataFromRequest
        }
    })
})

//methods
const updateColumnsConfig = (config: ColumnsObjectConfig) => {
    if (!config || !Object.values(config).length) {
        return
    }
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)
    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            columnsObjectConfig: config
        }
    }
    updateWidget(updatedWidgetData)
}
const updateColumnsAndHeadersConfig = (config: ColumnsObjectConfig, tableHeadersConfig: TableHeadersConfig) => {
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)
    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            ...(config && Object.values(config) ? { columnsObjectConfig: config } : {}),
            ...(tableHeadersConfig ? { tableHeadersConfig } : {})
        }
    }
    updateWidget(updatedWidgetData)
}

const updateWidget = (newData: WidgetExtended) => {
    emit('update-widget', newData)
}

const isCallsColum = (row: TableData, column: Column) => {
    return column.prop === callsKey || (row?.type === callsKey && Array.isArray(row[column.prop]))

}
const isRelatedStatisticColumn = (row: TableData, column: Column) => {
    return column.prop === relatedQueStatisticKeyName || (row?.type === relatedQueStatisticKeyName && Array.isArray(row[column.prop]))
}
</script>

<style lang="scss" scoped>
.tooltip-call-info {
  @apply border p-3;
  border-radius: 6px;
}
</style>

